import { z } from 'zod';
/**
 * Per-tenant correlation configuration.
 *
 * Correlation decides whether an incident EXISTS and how events group onto it.
 * That was previously a hardcoded global constant
 * (`DEFAULT_CORRELATION_CONFIG`), identical for every tenant and changeable
 * only by a deploy — so what happened after an incident existed was tunable per
 * tenant while what decided its existence was not.
 *
 * Validated structurally here for the same reason policy definitions are: a
 * malformed configuration could otherwise be activated and only fail when the
 * next event was correlated, by which point it is shaping incidents live.
 */
export const incidentTypeSchema = z.enum([
    'HIGH_RISK_DRIVER_BEHAVIOUR',
    'COMPLIANCE_RISK',
    'ASSET_AVAILABILITY_RISK',
    'VEHICLE_HEALTH_RISK',
    'FUEL_LOSS_RISK',
]);
const positiveMinutes = z.number().int().positive().max(10_080); // one week
const nonNegative = z.number().nonnegative();
export const overSpeedConfigSchema = z
    .object({
    minOverLimitKph: nonNegative,
    sustainedSeconds: z.number().int().nonnegative(),
    repeatedThreshold: z.number().int().positive(),
    repeatedWindowMinutes: positiveMinutes,
    mergeWindowMinutes: positiveMinutes,
})
    .strict();
export const geofenceExitConfigSchema = z
    .object({
    mergeWindowMinutes: positiveMinutes,
    /*
     * geofence.kind → incident type. `default` is mandatory: without it an
     * unrecognised kind classifies to undefined and the rule produces an
     * incident with no type.
     *
     * Intersected with an object requiring `default`, rather than checked by a
     * refine. A refine validates at runtime but leaves the INFERRED type a plain
     * Record, which then does not satisfy the domain's
     * `Record<string, IncidentType> & { default: IncidentType }` — and the
     * mismatch has to be papered over with a cast at every call site.
     */
    classificationMap: z
        /*
         * ⚠️ NULLABLE values: a kind may map to "raise nothing", which `DEPOT`
         * does by default. Additive, so every configuration stored before this
         * still parses — the schema is `.strict()` and a stricter value type would
         * have failed them all.
         */
        .record(incidentTypeSchema.nullable())
        .and(z.object({ default: incidentTypeSchema })),
})
    .strict();
export const batteryZeroConfigSchema = z
    .object({
    recentTelemetryMinutes: positiveMinutes,
    mergeWindowMinutes: positiveMinutes,
    unknownDefaultIncidentType: incidentTypeSchema,
})
    .strict();
export const harshDrivingConfigSchema = z
    .object({
    /** The client-configurable choice: one incident across the harsh types, or one each. */
    grouping: z.enum(['COMBINED', 'PER_TYPE']),
    subject: z.enum(['DRIVER_ELSE_VEHICLE', 'VEHICLE_ONLY']),
    // A ceiling as well as a floor: 5g is not a driving manoeuvre, it is a
    // crash or a broken sensor, and a threshold that high silently disables
    // the rule rather than failing loudly.
    minPeakG: z.number().positive().max(5),
    mergeWindowMinutes: positiveMinutes,
})
    .strict();
export const fuelConfigSchema = z
    .object({
    minDropLitres: z.number().positive(),
    /**
     * The floor for a drop OIP DERIVED from the percentage level, for fleets whose
     * provider sends no drop at all.
     *
     * ⚠️ Defaulted rather than required, like `alarmCorroborationMinutes` beside it:
     * this schema is `.strict()` and parses configurations stored before the field
     * existed, so a required one would fail every stored definition and fall back to
     * the platform default WHOLESALE — discarding every threshold the tenant had tuned.
     */
    minDropPct: z.number().min(2).max(100).default(10),
    dropWindowMinutes: positiveMinutes,
    requireStationaryForTheft: z.boolean(),
    refuelSuppressionMinutes: positiveMinutes,
    mergeWindowMinutes: positiveMinutes,
    /**
     * How far either side of a drop to look for a corroborating alarm (§8.5).
     *
     * Defaulted, not required, because the schema is `.strict()` and is used to
     * parse configurations STORED BEFORE this field existed. Required, an older
     * stored definition would fail to parse and the engine would fall back to
     * the platform default wholesale — silently discarding every setting the
     * tenant had tuned, with only a log line to show for it.
     */
    alarmCorroborationMinutes: positiveMinutes.default(15),
    /**
     * Whether the ABSENCE of an alarm may be stated as evidence of a leak.
     *
     * Leave false unless the provider guarantees alarm records survive buffer
     * pressure. Reading "no alarm received" as "no alarm happened" would route
     * a theft to a mechanic. Defaulted for the same backward-compatibility
     * reason as above — and it must default to the SAFE reading.
     */
    trustAlarmAbsence: z.boolean().default(false),
})
    .strict();
/**
 * The tracker's own security signals — tow alerts and immobiliser operation.
 *
 * `.default()` on the whole SECTION, for the reason `alarmCorroborationMinutes`
 * is defaulted one level down: this schema is `.strict()` and parses
 * configurations stored before the section existed. Required, every one of them
 * would fail to parse and the engine would fall back to the platform default
 * wholesale — silently discarding every setting the tenant had tuned.
 */
export const securitySignalConfigSchema = z
    .object({
    /**
     * How far either side of an immobiliser RELEASE to look for a tow or an
     * alarm corroborating it.
     *
     * A release on its own raises nothing — on a fleet that fits immobilisers
     * the driver releases one at the start of every shift, so an incident per
     * release is a rota rather than a security queue.
     */
    immobiliserCorroborationMinutes: positiveMinutes.default(30),
    /**
     * Whether a release with nothing corroborating it raises an incident anyway.
     *
     * `false`, and the default has to be the quiet one: a fleet whose drivers
     * release the immobiliser every morning would otherwise open one incident
     * per vehicle per day. Turn it on where the immobiliser is released only by
     * the office — then a release IS the finding.
     */
    raiseUncorroboratedRelease: z.boolean().default(false),
})
    .strict();
/**
 * Thresholds for the rules that turn telemetry into events.
 *
 * Every bound is deliberate rather than defensive. A seatbelt speed floor of 0
 * fires in yards and at every delivery stop; a sustained window of 0 fires on a
 * single sample. Both are the classic way a safety rule gets switched off in
 * its first week, so the schema refuses them rather than letting a well-meant
 * "make it more sensitive" produce an unusable queue.
 */
const seatbeltRuleSchema = z
    .object({
    minSpeedKph: z.number().min(1).max(200),
    sustainedSeconds: z.number().int().min(5).max(3600),
})
    .strict();
export const telemetryRulesConfigSchema = z
    .object({
    driverSeatbelt: seatbeltRuleSchema,
    passengerSeatbelt: seatbeltRuleSchema,
    alarm: z.object({ stationarySpeedKph: z.number().min(0).max(20) }).strict(),
    adblue: z
        .object({
        /** 0–3, the canonical ordered severity. 3 is the provider's "red". */
        minLevel: z.number().int().min(1).max(3),
        /**
         * ⚠️ Capped at 25, not 100. Above the provider's own "no action"
         * boundary this stops being a finding and becomes a dashboard reading —
         * and a queue holding a quarter of the fleet every week teaches the
         * reader to scroll past AdBlue rows, including the one at 2%.
         */
        refillBelowPct: z.number().min(1).max(25),
    })
        .strict(),
    pto: z
        .object({
        raiseWhileMoving: z.boolean(),
        minSpeedKph: z.number().min(1).max(200),
        sustainedSeconds: z.number().int().min(5).max(3600),
    })
        .strict(),
    /**
     * Drivers' hours.
     *
     * ⚠️ Only the DAILY and WEEKLY figures are thresholds here, and the split
     * is the design. The break and daily-rest alerts read the device's OWN
     * remaining time (`tachoTimeToBreakMin`, `tachoTimeToDailyRestMin`), so
     * they need no limit from us and are correct under assimilated/AETR AND GB
     * domestic without OIP being told which applies. The counters carry no such
     * remaining figure, so their limits must be stated — and they DIFFER
     * between rulesets, which is exactly why they are tenant configuration
     * rather than a platform constant.
     *
     * Defaults are the assimilated figures the customer supplied.
     */
    /**
     * ⚠️ `.default()`-ed, and it is load-bearing for the reason `security` and
     * `telemetryRules` itself already record — one level deeper, where it is
     * easier to miss.
     *
     * Every configuration stored before this section existed HAS a
     * `telemetryRules` block (seatbelt, alarm, AdBlue, PTO) and no
     * `driversHours`. The inner object is `.strict()`, so a REQUIRED section
     * here would make all of them fail to parse — and the engine falls back to
     * the platform default WHOLESALE on a parse failure, silently discarding
     * every threshold the tenant had tuned. Measured on the development
     * database at the moment this was added: 2 of 2 stored versions would have
     * broken.
     */
    driversHours: z
        .object({
        /** Minutes of remaining drive time at which a break is "approaching". */
        breakWarnWithinMin: z.number().int().min(1).max(120),
        /** Minutes of remaining duty at which a daily rest is "approaching". */
        restWarnWithinMin: z.number().int().min(1).max(240),
        /** Daily driving, in minutes: 9 h normally, 10 h extended. */
        dailyWarnMin: z.number().int().min(60).max(900),
        dailyLimitMin: z.number().int().min(60).max(900),
        dailyMaxMin: z.number().int().min(60).max(900),
        /** Weekly driving, in minutes: 56 h. */
        weeklyWarnMin: z.number().int().min(600).max(6000),
        weeklyLimitMin: z.number().int().min(600).max(6000),
    })
        .strict()
        .default({
        breakWarnWithinMin: 15,
        restWarnWithinMin: 30,
        dailyWarnMin: 510,
        dailyLimitMin: 540,
        dailyMaxMin: 600,
        weeklyWarnMin: 3000,
        weeklyLimitMin: 3360,
    }),
    /**
     * Driver card.
     *
     * ⚠️ `raiseMissingCard` defaults ON, unlike the PTO switch, because driving
     * with no card is a breach under every ruleset rather than a fleet-shaped
     * question — but the WORDING must carry the ambiguity: the provider
     * collapses the device's four-valued state upstream, so "no card" cannot be
     * told apart from a legitimate privacy-mode journey.
     */
    driverCard: z
        .object({
        raiseMissingCard: z.boolean(),
        /** Below this the vehicle is not "moving", so a card-out is not a finding. */
        minSpeedKph: z.number().min(1).max(200),
        raiseCardChange: z.boolean(),
        raiseNotRecognised: z.boolean(),
    })
        .strict()
        // Defaulted for the same reason as `driversHours` above.
        .default({
        raiseMissingCard: true,
        minSpeedKph: 5,
        raiseCardChange: true,
        raiseNotRecognised: true,
    }),
    /**
     * Deriving a fuel drop from the LEVEL, for fleets whose provider sends none.
     *
     * ⚠️ **Measured before building: 2 `FUEL_DROP` events against 7,276 hourly
     * fuel-level rows across 30 vehicles.** The event has only ever been
     * provider-pushed, so *"did any vehicle lose fuel?"* answered with an empty
     * table for almost every customer — which reads as **"no fuel was lost"**
     * rather than "nobody told us", the worst misreading available here.
     *
     * ⚠️ `.default()`-ed like every section above it, and for the same reason: this
     * schema is `.strict()` and parses configurations stored before the section
     * existed. Required, all of them would fail to parse — and a parse failure falls
     * back to the platform default WHOLESALE, discarding every threshold the tenant
     * had tuned. Measured when `driversHours` landed: 2 of 2 stored versions.
     */
    fuelDrop: z
        .object({
        /**
         * ⚠️ Floored at 2 rather than 0. A percentage sender is noisy and non-linear
         * near full and empty, and slosh on a slope moves a float gauge several points
         * on its own — below that the rule reports the road surface, which is how a
         * real signal gets switched off in its first week.
         */
        minDropPct: z.number().min(2).max(100),
        /**
         * ⚠️ Floored at 5 minutes, and the floor matters more than it looks. A window
         * shorter than a PARKED vehicle's reporting cadence can never hold two
         * readings, so there is nothing to compare against and the rule is silently
         * inert on exactly the vehicles it exists for — the platform's own state-gap
         * threshold is 30 minutes and `seed-demo-telemetry` parks at 20.
         */
        withinMinutes: z.number().int().min(5).max(720),
        stationaryOnly: z.boolean(),
        stationarySpeedKph: z.number().min(0).max(20),
    })
        .strict()
        .default({
        minDropPct: 10,
        withinMinutes: 30,
        stationaryOnly: true,
        stationarySpeedKph: 3,
    }),
})
    .strict();
export const correlationConfigSchema = z
    .object({
    version: z.string().trim().min(1).max(60),
    activeIncidentStatuses: z
        .array(z.enum(['OPEN', 'ACKNOWLEDGED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']))
        .min(1),
    overSpeed: overSpeedConfigSchema,
    geofenceExit: geofenceExitConfigSchema,
    batteryZero: batteryZeroConfigSchema,
    harshDriving: harshDrivingConfigSchema,
    fuel: fuelConfigSchema,
    security: securitySignalConfigSchema.default({
        immobiliserCorroborationMinutes: 30,
        raiseUncorroboratedRelease: false,
    }),
    /*
     * ⚠️ `.default()`-ed, and that is load-bearing for the same reason the
     * `security` section above is. This schema is `.strict()` and parses
     * configurations stored before the section existed; required, every one of
     * them would fail to parse — and the engine falls back to the PLATFORM
     * default wholesale on a parse failure, silently discarding every threshold
     * the tenant had tuned. A required section here would quietly reset every
     * customer who had ever saved one.
     */
    telemetryRules: telemetryRulesConfigSchema.default({
        driverSeatbelt: { minSpeedKph: 20, sustainedSeconds: 120 },
        passengerSeatbelt: { minSpeedKph: 20, sustainedSeconds: 120 },
        alarm: { stationarySpeedKph: 3 },
        adblue: { minLevel: 3, refillBelowPct: 15 },
        pto: { raiseWhileMoving: true, minSpeedKph: 15, sustainedSeconds: 30 },
        fuelDrop: { minDropPct: 10, withinMinutes: 30, stationaryOnly: true, stationarySpeedKph: 3 },
        driversHours: {
            breakWarnWithinMin: 15,
            restWarnWithinMin: 30,
            // 8h30 warn, 9h limit, 10h extended maximum — the assimilated figures.
            dailyWarnMin: 510,
            dailyLimitMin: 540,
            dailyMaxMin: 600,
            // ~50h warn, 56h limit.
            weeklyWarnMin: 3000,
            weeklyLimitMin: 3360,
        },
        driverCard: {
            raiseMissingCard: true,
            minSpeedKph: 5,
            raiseCardChange: true,
            raiseNotRecognised: true,
        },
    }),
})
    .strict();
export const createCorrelationConfigVersionRequestSchema = z
    .object({
    definition: correlationConfigSchema,
    /** Optional explicit version number; the server assigns the next if omitted. */
    version: z.number().int().positive().optional(),
})
    .strict();
export const correlationConfigVersionSchema = z.object({
    id: z.string().uuid(),
    version: z.number().int(),
    isActive: z.boolean(),
    /**
     * When this version FIRST went live, or null if it never did.
     *
     * ⚠️ The distinction `isActive` cannot make. A version superseded last month
     * and a version created and abandoned are both `isActive: false`, and they
     * are opposite things — the first is the record of what the rules WERE when
     * incidents were judged under them, the second is a typo. The settings list
     * showed both as "Draft" beside an Activate button.
     */
    activatedAt: z.string().nullable(),
    /**
     * Whether this version may be discarded, DERIVED rather than stored.
     *
     * Two fields that must never disagree eventually will — the same reason
     * `editRefusal` derives from `discardable` on a policy version rather than
     * being a second flag.
     */
    discardable: z.boolean(),
    definition: z.record(z.unknown()),
    createdAt: z.string(),
});
export const correlationConfigVersionListSchema = z.object({
    items: z.array(correlationConfigVersionSchema),
    /**
     * True when no version has been stored and the platform default is in force.
     * Distinguishes "never configured" from "configured to the same values",
     * which matters when deciding whether an edit is a first configuration.
     */
    usingPlatformDefault: z.boolean(),
});
// ── dry run ─────────────────────────────────────────────────────────────────
/**
 * Replay the DETECTION thresholds against telemetry that already arrived.
 *
 * ⚠️ **Detection only, and the omission is the honest part.** The settings on
 * this page fall into two halves and only one can be replayed faithfully:
 *
 *  - `telemetryRules` turns samples into events through `evaluateTelemetryRules`,
 *    which is PURE — samples in, events out, no context. The samples are stored,
 *    so a replay is the real computation on the real input.
 *  - The correlation sections turn events into incidents, and a correlation rule
 *    reads a `CorrelationContext` carrying the ACTIVE INCIDENT, the geofence and
 *    vehicle state. Those have moved on, and rebuilding them would answer with a
 *    context that never existed — the same reason the policy backtest replays
 *    recorded `valuesUsed` rather than reconstructing history.
 *
 * A number for the half that cannot be computed would be confident and wrong,
 * so the response names what it did NOT cover instead of quietly excluding it.
 */
export const dryRunRequestSchema = z
    .object({
    telemetryRules: telemetryRulesConfigSchema,
    /** Bounded by telemetry retention; asking for more is refused, not silently clipped. */
    days: z.number().int().min(1).max(30).default(7),
})
    .strict();
export const dryRunCountSchema = z.object({
    eventType: z.string(),
    /** What the settings IN FORCE would have produced over the same samples. */
    current: z.number().int(),
    /** What the settings being edited would have produced. */
    candidate: z.number().int(),
});
export const dryRunResponseSchema = z.object({
    counts: z.array(dryRunCountSchema),
    windowDays: z.number().int(),
    samplesRead: z.number().int(),
    vehiclesRead: z.number().int(),
    /**
     * Every vehicle that reported in the window, whether or not it was read.
     *
     * ⚠️ The DENOMINATOR, so "50 of 88 vehicles" can be stated. A count with no
     * denominator reads as the whole fleet, and a threshold that looks quiet
     * because two thirds of the fleet was skipped is the silent-truncation
     * failure this codebase keeps meeting.
     */
    vehiclesTotal: z.number().int(),
    truncated: z.boolean(),
    /**
     * Settings this replay did NOT cover, named rather than omitted.
     *
     * A reader who sees "no change" must be able to tell "these settings make no
     * difference" from "these settings were not tested".
     */
    notCovered: z.array(z.string()),
});
//# sourceMappingURL=correlation-config.js.map